import * as React from 'react';
import * as cx from 'classnames';

import { noDefault } from 'lib/eventWrappers';
import SuperCritical from 'lib/supercritical';
import TZDate from 'lib/TZDate';
import { tzmoment } from 'lib/tzmoment';

import { BatchRecurrenceFrequency, BatchRecurrenceFrequencys } from 'server/enums/BatchRecurrenceFrequency';
import { BatchRecurrencePeriod } from 'data/models/BatchRecurrencePeriod';
import { Location } from 'data/models/Location';

import {
    AddPortCallItineraryTab as ItineraryTab,
    BATCH_VALIDATION_KEY_CUSTOM_DAYS,
    BATCH_VALIDATION_KEY_STOP_DATE,
    ReservationBuilder
} from './itinerary';

import { BerthSelectorView } from 'portcall/berthselector/view';
import { DatePicker, NullableDatePicker, ProgInitDateTimePicker } from 'portcall/datetimepicker/view';
import { NumericInput } from 'portcall/numericinput/view';
import { PortCallScheduleHeaderView } from 'portcall/portcallschedule/view';
import { ValidationErrorView } from 'core/validationerror/view';

// It's just fine if this overshoots the actual number. Good to leave extra room.
const TAB_STOPS_PER_BUILDER_ROW = 5;

export const AddPortCallItineraryTabView = SuperCritical(({ model: m } : { model : ItineraryTab }) =>
    <div className="addportcallitinerarytab">
        <PortCallScheduleHeaderView
            className="schedule-header"
            model={m.ScheduleHeader}
            includeItinerary={false}
        />
        <ItineraryBuilder model={m} />
        <RepeatPortCall model={m} />
    </div>
);

const ItineraryBuilder = SuperCritical(({ model: m } : { model : ItineraryTab }) =>
    <div className="itinerary-builder">
        <label className="itinerary-builder-label">Itinerary</label>
        <div className="itinerary-builder-content">
            <ItineraryBuilderInstructions model={m} />
            <ItineraryBuilderBody model={m} />
        </div>
    </div>
);

const ItineraryBuilderInstructions = SuperCritical(({ model: m } : { model : ItineraryTab }) => {
    return (
        <div className="itinerary-builder-instructions">
            {
                m.PortArrivalDate() === null ?
                    'Start with Arrival ETA' :
                ((m.FirstBuilder() as ReservationBuilder)?.Berth() ?? null) === null ?
                    'Enter the Arrival location' :
                ((m.FirstBuilder() as ReservationBuilder)?.LastLine() ?? null) === null ?
                    'Indicate the ETD' :
                    'Add a shift, repeat the portcall or move on'
            }
        </div>
    );
});

const ItineraryBuilderBody = SuperCritical(({ model: m } : { model : ItineraryTab }) => {
    const 
        builders = (m.ReservationBuilders() as ReservationBuilder[]),
        rows : React.ReactElement<any>[] = [
            <FirstArrivalRow key="first-arrival" model={m} tabIndexStart={1} />
        ];

    for (let i = 0; i < builders.length; i++) {
        const
            builder = builders[i],
            nextBuilder = i === builders.length - 1 ? null : builders[i + 1];
        if (builder.Berth()) {
            rows.push(
                <FirstLineRow
                    key={`first-line-${builder.Key}`}
                    model={m}
                    builder={builder}
                    tabIndexStart={(2 * i + 1) * TAB_STOPS_PER_BUILDER_ROW + 1}
                />,
                <LastLineRow
                    key={`last-line-${builder.Key}`}
                    model={m}
                    builder={builder}
                    nextBuilder={nextBuilder}
                    tabIndexStart={2 * (i + 1) * TAB_STOPS_PER_BUILDER_ROW + 1}
                />
            );
        }
    }

    return (
        <div className="itinerary-builder-body">{rows}</div>
    );
});

const FirstArrivalRow = SuperCritical(({ model: m, tabIndexStart } : { model : ItineraryTab, tabIndexStart : number }) =>
    <div className="itinerary-builder-row first-arrival-row">
        <div className={cx('date-picker-shell', { 'invalid-date': !m.IsPortArrivalDateValid() })}>
            <div>
                <ProgInitDateTimePicker
                    value={m.PortArrivalDate}
                    localization={m.LocalizationContext}
                    tabindex={{ date: tabIndexStart, time: tabIndexStart + 1 }}
                    autofocus={{ date: true, time: false }}
                    isAddPortcall={true}
                />
            </div>
            <div className="label-below">First Arrival</div>
        </div>
        <div className="row-text">
            <b>
                {/* TODO: Instead of hard coding Pilot Area as a fallback, this would make a great column on
                dbo.DomainSettings, say under PierSettings */}
                {LocationNameText(m.PortArrivalPoint()?.Location()) ?? 'Pilot Area'}
            </b>
            {' '}
            &mdash; inbound to
        </div>
        {m.FirstBuilder() &&
            <div className="berth-selector-shell">
                <BerthSelectorView
                    className="search-filter"
                    model={(m.FirstBuilder() as ReservationBuilder)!.BerthSelector}
                    tabIndex={tabIndexStart + 2}
                />
            </div>
        }
        {/* NOTE: Intentionally not providing a link to delete the first builder. Our CanProceed logic requires
        users to build at least one reservation anyway. */}
    </div>
);

const FirstLineRow = SuperCritical(({ model: m, builder, tabIndexStart } : {
    model : ItineraryTab,
    builder : ReservationBuilder,
    tabIndexStart : number
}) =>
    <div className="itinerary-builder-row first-line-row">
        <div className={cx('date-picker-shell', { 'invalid-date': !builder.IsFirstLineValid() })}>
            <div>
                <ProgInitDateTimePicker
                    value={builder.FirstLine}
                    localization={m.LocalizationContext}
                    autofocus={{ date: true }}
                    tabindex={{ date: tabIndexStart, time: tabIndexStart + 1 }}
                />
            </div>
            <div className="label-below">First Line</div>
        </div>
        <div className="row-text">
            Arriving
            {' '}
            <b>{LocationNameText(builder.Berth()?.Location())}</b>
        </div>
    </div>
);

const LastLineRow = SuperCritical(({ model: m, builder, nextBuilder, tabIndexStart } : {
    model : ItineraryTab,
    builder : ReservationBuilder,
    nextBuilder : ReservationBuilder | null,
    tabIndexStart : number
}) =>
    <div className="itinerary-builder-row last-line-row">
        <div className={cx('date-picker-shell', { 'invalid-date': !builder.IsLastLineValid() })}>
            <div>
                <ProgInitDateTimePicker
                    value={builder.LastLine}
                    localization={m.LocalizationContext}
                    tabindex={{ date: tabIndexStart, time: tabIndexStart + 1 }}
                    isAddPortcall={true}
                />
            </div>
            <div className="label-below">Last Line</div>
        </div>
        <div className="row-text">
            Departing
            {' '}
            <b>{LocationNameText(builder.Berth()?.Location())}</b>
            {' '}
            &mdash;
            {nextBuilder ? ' shifting to' : ' outbound'}
        </div>
        {
            nextBuilder ?
                <div className="berth-selector-shell">
                    <div>
                        <BerthSelectorView
                            className="search-filter"
                            model={nextBuilder.BerthSelector}
                            tabIndex={tabIndexStart + 2}
                        />
                    </div>
                    {!nextBuilder.IsPortValid() &&
                        <div className="invalid-port-msg">
                            Location must match the first location's port/region.
                        </div>
                    }
                    <div className="cancel-shift-shell">
                        <a
                            href=""
                            className="cancel-shift-link"
                            onClick={noDefault(nextBuilder.Delete)}
                        >
                            Cancel Shift
                        </a>
                    </div>
                </div> :
            builder.Reservation() ?
                <div className="add-shift-shell">
                    <a
                        href=""
                        className="add-shift-link"
                        onClick={noDefault(m.AddReservationBuilder)}
                    >
                        + Add Shift
                    </a>
                </div> :
            null
        }
    </div>
);

function LocationNameText(loc : Location | null | undefined) {
    return loc && `${loc.Name()} (${loc.Port()?.Name() ?? 'loading...'})`;
}

const RepeatPortCall = SuperCritical(({ model: m } : { model : ItineraryTab }) => {
    // 2 rows per reservation builder plus a row for the port arrival date.
    const nextTabIndex = (2 * m.ReservationBuilders().length + 1) * TAB_STOPS_PER_BUILDER_ROW + 1;
    return (
        m.BatchRequest() ?
            <div className="repeat-pc-section">
                <RepeatPortCallHeader model={m} />
                <RepeatPortCallBody model={m} tabIndexStart={nextTabIndex} />
            </div> :
        m.CanRepeatPortCall() ?
            <div className="repeat-pc-link-shell">
                <a
                    href=""
                    className="repeat-pc-link"
                    onClick={noDefault(() => m.RepeatPortCall(true))}
                >
                    Repeat PortCall
                </a>
            </div> :
        null
    );
});

const RepeatPortCallHeader = SuperCritical(({ model: m } : { model : ItineraryTab }) =>
    <div className="repeat-pc-header">
        <label className="repeat-pc-title">Range</label>
        <a
            href=""
            className="cancel-repeat-link"
            onClick={noDefault(() => m.RepeatPortCall(false))}
        >
            Cancel repeating PortCalls
        </a>
    </div>
);

const RepeatPortCallBody = SuperCritical(({ model: m, tabIndexStart } : {
    model : ItineraryTab,
    tabIndexStart : number
}) =>
    <div className="repeat-pc-body">
        <RepeatPortCallDates model={m} tabIndexStart={tabIndexStart} />
        <RepeatPortCallFrequency model={m} tabIndexStart={tabIndexStart + 5} />
    </div>
);

const RepeatPortCallDates = SuperCritical(({ model: m, tabIndexStart } : {
    model: ItineraryTab,
    tabIndexStart : number
}) =>
    <div className="repeat-pc-dates">
        <div>
            <div className="start-date-shell">
                <div>
                    <NullableDatePicker
                        value={m.PortArrivalDate}
                        disabled={true}
                        localization={m.LocalizationContext}
                    />
                </div>
                <div className="label-below">Start</div>
            </div>
            {m.BatchRequest() &&
                <div className="stop-date-shell">
                    <div>
                        <DatePicker
                            value={m.BatchRequest()!.StopDate}
                            localization={m.LocalizationContext}
                            tabindex={tabIndexStart}
                        />
                    </div>
                    <div className="label-below">Stop</div>
                </div>
            }
        </div>
        <ValidationErrorView error={() => m.BatchValidation()[BATCH_VALIDATION_KEY_STOP_DATE]} />
    </div>
);

const RepeatPortCallFrequency = SuperCritical(({ model: m, tabIndexStart } : {
    model : ItineraryTab,
    tabIndexStart : number
}) =>
    <div className="repeat-pc-frequency">
        <FrequencyDropdown model={m} tabIndex={tabIndexStart} />
        <FrequencyDescription model={m} tabIndex={tabIndexStart + 1} />
        <ValidationErrorView error={() => m.BatchValidation()[BATCH_VALIDATION_KEY_CUSTOM_DAYS]} />
    </div>
);

const FrequencyDropdown = SuperCritical(({ model: m, tabIndex } : {
    model : ItineraryTab,
    tabIndex : number
}) =>
    m.BatchRequest()?.Batch() ?
        <div className="frequency-shell">
            <select
                className="pc-select frequency-select"
                value={m.BatchRequest()!.Batch()!.RecurrencePeriod.Frequency()}
                onChange={e => m.BatchRequest()!.Batch()!.RecurrencePeriod.Frequency(+e.currentTarget.value)}
                tabIndex={tabIndex}
            >
                {BatchRecurrenceFrequencys.map(({ Id, Name }) =>
                    <option value={Id} key={Id}>{Name}</option>
                )}
            </select>
            <span className="pc-select-chevron"></span>
        </div> :
        null
);

const FrequencyDescription = SuperCritical(({ model: m, tabIndex } : {
    model : ItineraryTab,
    tabIndex : number
}) =>
    <div className="frequency-description label-below">
        Frequency |
        {' '}
        {m.BatchRequest()?.Batch() && (
            m.BatchRequest()?.Batch()!.RecurrencePeriod.Frequency() === BatchRecurrenceFrequency.Custom ?
                <span>
                    Repeats every
                    {' '}
                    <NumericInput
                        className="custom-days"
                        value={m.BatchRequest()!.Batch()!.RecurrencePeriod.CustomDays}
                        tabindex={tabIndex}
                    />
                    {' '}
                    days.
                </span> :
                <span>
                    {frequencyDescriptionText(m.PortArrivalDate(), m.BatchRequest()!.Batch()!.RecurrencePeriod)}
                </span>
        )}
    </div>
);

export function frequencyDescriptionText(startDate : TZDate | null, recurrencePeriod : BatchRecurrencePeriod) {
    const frequency = recurrencePeriod.Frequency();
    if (frequency === BatchRecurrenceFrequency.Daily) {
        return 'Repeats every day.';
    } else if (frequency === BatchRecurrenceFrequency.Weekly) {
        const format = 'dddd'; // Full day of week name
        return `Repeats every week${startDate ? ` on ${tzmoment(startDate).format(format)}` : ''}.`;
    } else if (frequency === BatchRecurrenceFrequency.Monthly) {
        const format = 'Do'; // Ordinal day of month
        return `Repeats every month${startDate ? ` on the ${tzmoment(startDate).format(format)}` : ''}.`;
    } else if (frequency === BatchRecurrenceFrequency.Custom) {
        return `Repeats every ${recurrencePeriod.CustomDays() === 1 ? 'day' : `${recurrencePeriod.CustomDays()} days`}.`;
    } else {
        throw new Error(`Unknown BatchRecurrenceFrequency value ${frequency}`);
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/itineraryview.tsx